import { useMemo } from "react";
import { VelavuDevice } from "velavu-js-api";
import { serializeAssetCategorization } from "../../data/asset-categorization";

export interface UseSortedCategorizationsParams {
	devices: readonly VelavuDevice[] | undefined;
	selectedCategorizations: readonly string[];
}

export function useSortedCategorizations(
	params: UseSortedCategorizationsParams,
): string[] | undefined {
	// An array of serialized asset categorizations to the number of times they are used
	const allCategorizations = useMemo((): [string, number][] | undefined => {
		if (params.devices === undefined) return undefined;

		return Object.entries(
			params.devices.reduce<{ [key: string]: number }>(
				(accumulator, device) => {
					if (device.asset === undefined) {
						return accumulator;
					}

					const key = serializeAssetCategorization(device.asset);
					accumulator[key] = (accumulator[key] ?? 0) + 1;
					return accumulator;
				},
				{},
			),
		);
	}, [params.devices]);

	//Sort selected categorizations to the front of the list, and then sort by usage count
	const sortedCategorizations = useMemo(() => {
		return allCategorizations
			?.sort(([keyA, countA], [keyB, countB]) => {
				const aSelected = params.selectedCategorizations.includes(keyA);
				const bSelected = params.selectedCategorizations.includes(keyB);

				if (aSelected && !bSelected) return -1;
				else if (bSelected && !aSelected) return 1;

				const countDiff = countB - countA;
				if (countDiff !== 0) return countDiff;
				else return keyA.localeCompare(keyB);
			})
			.map(([key]) => key);
	}, [allCategorizations, params.selectedCategorizations]);

	return sortedCategorizations;
}
